/**
 * ¿Una capa 2D (DWG/DXF) se mide como MURO? — y la lista de las que lo parecen y no lo son.
 *
 * El lector de planos (`plano2svg.py`) decide por GEOMETRÍA: dos líneas paralelas cercanas y largas
 * son un muro de doble trazo (`cuantificacion.fuente = 'eje_muro'`). Pero unos ejes de retícula, una
 * herrería o un drenaje dibujados así son geométricamente idénticos a un muro: la forma no los
 * distingue. Lo que los distingue es QUÉ ES la capa, y eso lo dice la IA (`clasificarCapasPlano` en el
 * worker), nunca una lista de nombres. Este módulo junta las dos cosas en un solo lugar:
 *
 *  - `capaSeMideComoMuro`: la IA la reconoció como muro, o no hay clasificación (comportamiento de
 *    siempre). La corrección de la persona (el ⋯ de cada capa) manda: «Es solo dibujo» la saca;
 *    «Se construye» sobre una capa que la IA había descartado la devuelve a la geometría.
 *  - `capasQueParecenMuro`: las capas que el lector midió como muro y la IA dice que no lo son, con
 *    sus metros y el porqué de la IA, para «Lo que se dedujo».
 */
import type { CapasClasificadasDTO, DecisionDeCapa, NaturalezaCapa, PlanoInventario } from './domain.js';
import { decisionDeCapa, naturalezaDeCapa } from './domain.js';

/**
 * @param capa nombre de la capa en el dibujo.
 * @param clasif clasificación de la IA del plano (null = no hay → la geometría decide, como siempre).
 * @param correccion lo que eligió la persona en el ⋯ de la capa (undefined = no tocó nada).
 */
export function capaSeMideComoMuro(
  capa: string,
  clasif: CapasClasificadasDTO | null | undefined,
  correccion?: DecisionDeCapa | null,
): boolean {
  if (correccion === 'ruido') return false;
  // Rescató una capa que la IA había mandado a no cotizar (o a la duda): vuelve a decidir la forma.
  if (correccion === 'cotiza' && decisionDeCapa(capa, clasif) !== 'cotiza') return true;
  const nat = naturalezaDeCapa(capa, clasif);
  return nat === null || nat === 'muro';
}

/** Una capa con forma de muro que no se mide como muro. */
export interface CapaConFormaDeMuro {
  capa: string;
  /** Largo por el centro (lo que habría sumado como muro), en metros. null = sin escala. */
  metros: number | null;
  naturaleza: NaturalezaCapa;
  /** `ruido` = no se cotiza (ejes, cotas…); `cotiza` = es otra obra (escalera, herrería…), no muro. */
  decision: DecisionDeCapa | null;
  /** Por qué, en palabras de la IA (solo para las de ruido; cachés viejas no lo traen). */
  motivo: string | null;
  /** La decisión es de la persona (el ⋯), no de la IA. */
  corregida: boolean;
}

/**
 * Las capas que el lector midió como muro de doble trazo y la IA reconoció como otra cosa, de más a
 * menos metros. Sin clasificación devuelve []: no hay nada deducido que mostrar. Las que la persona
 * rescató con el ⋯ («Se construye») no salen: ya no se descartan.
 */
export function capasQueParecenMuro(
  inventario: Pick<PlanoInventario, 'capas'> | null | undefined,
  clasif: CapasClasificadasDTO | null | undefined,
  factor: number | null,
  correcciones: Readonly<Record<string, DecisionDeCapa>> = {},
): CapaConFormaDeMuro[] {
  if (!inventario || !clasif) return [];
  const out: CapaConFormaDeMuro[] = [];
  for (const c of inventario.capas) {
    if (c.cuantificacion?.fuente !== 'eje_muro' || !((c.corrido ?? 0) > 0)) continue;
    if (capaSeMideComoMuro(c.capa, clasif, correcciones[c.capa])) continue;
    const naturaleza = naturalezaDeCapa(c.capa, clasif);
    if (naturaleza === null || naturaleza === 'muro') continue;
    const metros = factor != null && Number.isFinite(factor) && factor > 0 ? Math.round(c.corrido! * factor * 100) / 100 : null;
    out.push({
      capa: c.capa,
      metros,
      naturaleza,
      decision: correcciones[c.capa] ?? decisionDeCapa(c.capa, clasif),
      motivo: clasif.motivos?.[c.capa] ?? null,
      corregida: correcciones[c.capa] != null,
    });
  }
  return out.sort((a, b) => (b.metros ?? 0) - (a.metros ?? 0));
}
